import { generateMockReceipts } from '@/data/payments-mock-data';
import { graphqlQuery } from '@/lib/api/graphql';
import {
  buildGraphQLSortVariable as buildSort,
  computeMockPagination,
  computePaginationWithCount,
  createDefaultPagination,
  type GraphQLConnection,
  type PaginationResult,
  pageToCursor,
} from '@/lib/graphql-pagination';
import { useApiConfig } from '@/providers/ApiConfigProvider';
import type { AllocationStatus, PaymentMethod, Receipt } from '@/types/payments';
import { useModeAwareQuery } from './useModeAwareQuery';

export type { PaginationResult } from '@/lib/graphql-pagination';

// --- GraphQL types ---

export interface ReceiptGraphQLNode {
  transactionId: number;
  referenceNumber: string | null;
  customerId: number;
  customerName: string | null;
  accountNumber: string | null;
  creditControllerId: number | null;
  transactionDate: string;
  amount: number;
  outstandingAmount: number;
  totalAllocated: number;
  currencyCode: string | null;
  paymentMethodId: number | null;
  paymentMethodCode: string | null;
  paymentMethodName: string | null;
  transactionReference: string | null;
  bankReference: string | null;
  allocationStatusCode: string | null;
  allocationStatusName: string | null;
  allocationCount: number;
  externalId: string | null;
  importedAt: string | null;
  importBatchId: string | null;
  transactionNotes: string | null;
  currencyAmount: number | null;
  currencyOutstandingAmount: number | null;
  displayTotalAllocated: number | null;
  hasPromise: boolean;
  onDispute: boolean;
  hasDispute: boolean;
  createdByName: string | null;
  transactionCreatedAt: string;
  modifiedByName: string | null;
  transactionModifiedAt: string | null;
}

export interface ReceiptListGraphQLResponse {
  receipts: {
    receiptList: GraphQLConnection<ReceiptGraphQLNode>;
  };
}

interface ReceiptCountGraphQLResponse {
  receipts: {
    receiptCount: number;
  };
}

export interface ReceiptTotalsData {
  totalCount: number;
  totalAmount: number;
  totalOutstanding: number;
  unallocatedCount: number;
  unallocatedAmount: number;
  fullyAllocatedCount: number;
  fullyAllocatedAmount: number;
  partiallyAllocatedCount: number;
  partiallyAllocatedAmount: number;
  partiallyAllocatedOutstandingAmount: number;
  totalCurrencyAmount: number;
  unallocatedCurrencyAmount: number;
  fullyAllocatedCurrencyAmount: number;
  partiallyAllocatedCurrencyAmount: number;
  partiallyAllocatedOutstandingCurrencyAmount: number;
}

export interface ReceiptTotalsGraphQLResponse {
  receipts: {
    receiptTotals: ReceiptTotalsData;
  };
}

// --- GraphQL queries ---

const RECEIPT_NODE_FIELDS = `
  transactionId
  referenceNumber
  customerId
  customerName
  accountNumber
  creditControllerId
  transactionDate
  amount
  outstandingAmount
  totalAllocated
  currencyCode
  paymentMethodId
  paymentMethodCode
  paymentMethodName
  transactionReference
  bankReference
  allocationStatusCode
  allocationStatusName
  allocationCount
  externalId
  importedAt
  importBatchId
  transactionNotes
  currencyAmount
  currencyOutstandingAmount
  displayTotalAllocated
  hasPromise
  onDispute
  hasDispute
  createdByName
  transactionCreatedAt
  modifiedByName
  transactionModifiedAt
`;

const RECEIPT_LIST_QUERY = `
  query ReceiptList($first: Int, $after: String, $sort: [ReceiptListSortSortByInput!], $filter: ReceiptListFilterInput) {
    receipts {
      receiptList(first: $first, after: $after, sort: $sort, filter: $filter) {
        edges {
          node {
            ${RECEIPT_NODE_FIELDS}
          }
        }
        pageInfo {
          startCursor
          endCursor
          hasNextPage
          hasPreviousPage
        }
      }
    }
  }
`;

const RECEIPT_COUNT_QUERY = `
  query ReceiptCount($filter: ReceiptListFilterInput) {
    receipts {
      receiptCount(filter: $filter)
    }
  }
`;

export const RECEIPT_TOTALS_QUERY = `
  query ReceiptTotals($filter: ReceiptListFilterInput) {
    receipts {
      receiptTotals(filter: $filter) {
        totalCount
        totalAmount
        totalOutstanding
        unallocatedCount
        unallocatedAmount
        fullyAllocatedCount
        fullyAllocatedAmount
        partiallyAllocatedCount
        partiallyAllocatedAmount
        partiallyAllocatedOutstandingAmount
        totalCurrencyAmount
        unallocatedCurrencyAmount
        fullyAllocatedCurrencyAmount
        partiallyAllocatedCurrencyAmount
        partiallyAllocatedOutstandingCurrencyAmount
      }
    }
  }
`;

// --- Mapping ---

function mapAllocationStatusCode(code: string | null): AllocationStatus {
  switch (code) {
    case 'fully_allocated':
      return 'fully_allocated';
    case 'partially_allocated':
      return 'partially_allocated';
    case 'unallocated':
      return 'unallocated';
    case 'over_allocated':
      return 'over_allocated';
    default:
      return 'unallocated';
  }
}

function mapPaymentMethodCode(code: string | null): PaymentMethod {
  switch (code) {
    case 'bank_transfer':
      return 'bank_transfer';
    case 'credit_card':
      return 'credit_card';
    case 'direct_debit':
      return 'direct_debit';
    case 'cheque':
      return 'cheque';
    case 'cash':
      return 'cash';
    default:
      return 'bank_transfer';
  }
}

export function mapGraphQLNodeToReceipt(node: ReceiptGraphQLNode, homeCurrency?: string): Receipt {
  const home = homeCurrency || 'GBP';
  const isNonHomeCurrency = node.currencyCode != null && node.currencyCode !== home;
  return {
    id: node.transactionId,
    receiptNumber: node.referenceNumber || String(node.transactionId),
    customerId: node.customerId,
    customerName: node.customerName || '',
    customerAccountNumber: node.accountNumber || '',
    creditControllerId: node.creditControllerId === null ? undefined : String(node.creditControllerId),
    receiptDate: node.transactionDate ? new Date(node.transactionDate) : new Date(),
    receivedAmount: isNonHomeCurrency ? (node.currencyAmount ?? 0) : node.amount,
    allocatedAmount: node.displayTotalAllocated ?? node.totalAllocated,
    unappliedAmount: isNonHomeCurrency ? (node.currencyOutstandingAmount ?? 0) : node.outstandingAmount,
    currency: node.currencyCode || home,
    homeReceivedAmount: isNonHomeCurrency ? node.amount : undefined,
    homeUnappliedAmount: isNonHomeCurrency ? node.outstandingAmount : undefined,
    paymentMethod: mapPaymentMethodCode(node.paymentMethodCode),
    paymentReference: node.transactionReference || '',
    bankReference: node.bankReference || undefined,
    externalId: node.externalId || '',
    importedAt: node.importedAt ? new Date(node.importedAt) : new Date(),
    importBatchId: node.importBatchId || undefined,
    notes: node.transactionNotes || undefined,
    allocationStatus: mapAllocationStatusCode(node.allocationStatusCode),
    hasPromise: node.hasPromise,
    onDispute: node.onDispute,
    hasDispute: node.hasDispute,
    createdBy: node.createdByName || '',
    createdAt: node.transactionCreatedAt ? new Date(node.transactionCreatedAt) : new Date(),
    modifiedBy: node.modifiedByName || undefined,
    modifiedAt: node.transactionModifiedAt ? new Date(node.transactionModifiedAt) : undefined,
  };
}

// --- Sort / Filter builders ---

export interface GetReceiptsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  customerId?: string;
  creditControllerId?: string;
  currency?: string;
  allocationStatus?: string;
  metricAllocationStatus?: string;
  dateFrom?: string;
  dateTo?: string;
  amountMin?: number;
  amountMax?: number;
  outstandingMin?: number;
  outstandingMax?: number;
  outstandingNotZero?: boolean;
  hasPromise?: boolean;
  onDispute?: boolean;
  hasDispute?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Amount columns sort, filter and export on the Display* fields: the receipts grid
// always shows the transaction currency, so the base-currency fields would order and
// match rows against values the user cannot see.
export const sortFieldMap: Record<string, string> = {
  receiptNumber: 'ReferenceNumber',
  customerAccountNumber: 'AccountNumber',
  customerName: 'CustomerName',
  receiptDate: 'TransactionDate',
  receivedAmount: 'DisplayAmount',
  unappliedAmount: 'DisplayOutstandingAmount',
  allocationStatus: 'AllocationStatusName',
};

function buildGraphQLSortVariable(sortBy?: string, sortOrder?: 'asc' | 'desc') {
  return buildSort(sortFieldMap, sortBy, sortOrder);
}

function buildNumericListFilter(value?: string): Record<string, unknown> | undefined {
  if (!value) return undefined;
  const values = value.split(',').map(Number);
  if (values.some((id) => !Number.isInteger(id) || id <= 0)) return undefined;
  return values.length === 1 ? { eq: values[0] } : { containsValues: values };
}

function buildStringListFilter(value?: string): Record<string, unknown> | undefined {
  if (!value || value === 'all') return undefined;
  const values = value.split(',');
  return values.length === 1 ? { eq: values[0] } : { containsValues: values };
}

function buildRangeFilter<T>(minimum?: T, maximum?: T): Record<string, T> | undefined {
  const filter: Record<string, T> = {};
  if (minimum !== undefined) filter.ge = minimum;
  if (maximum !== undefined) filter.le = maximum;
  return Object.keys(filter).length > 0 ? filter : undefined;
}

export function buildGraphQLFilterVariable(params: GetReceiptsParams) {
  const filter: Record<string, unknown> = {
    summary: params.search,
    customerId: buildNumericListFilter(params.customerId),
    creditControllerId: buildNumericListFilter(params.creditControllerId),
    allocationStatusCode: buildStringListFilter(params.metricAllocationStatus ?? params.allocationStatus),
    displayAmount: buildRangeFilter(params.amountMin, params.amountMax),
    transactionDate: buildRangeFilter(params.dateFrom, params.dateTo),
    currencyCode: buildStringListFilter(params.currency),
    displayOutstandingAmount: buildRangeFilter(params.outstandingMin, params.outstandingMax),
    // Currency-independent allocation check, so it stays on the base outstanding amount.
    outstandingAmount: params.outstandingNotZero ? { ne: 0 } : undefined,
    hasPromise: params.hasPromise === undefined ? undefined : { eq: params.hasPromise },
    onDispute: params.onDispute === undefined ? undefined : { eq: params.onDispute },
    hasDispute: params.hasDispute === undefined ? undefined : { eq: params.hasDispute },
  };

  return Object.fromEntries(Object.entries(filter).filter(([, value]) => value !== undefined));
}

export function buildReceiptFilterQueryKey(params: GetReceiptsParams = {}): GetReceiptsParams {
  const key = { ...params };
  delete key.page;
  delete key.pageSize;
  delete key.sortBy;
  delete key.sortOrder;
  return key;
}

export function buildReceiptTotalsParams(params: GetReceiptsParams = {}): GetReceiptsParams {
  const totalsParams = { ...params };
  delete totalsParams.metricAllocationStatus;
  return totalsParams;
}

// --- Mock helpers ---

function getMockReceiptsFromStore(): Receipt[] {
  return generateMockReceipts();
}

function filterMockReceipts(receipts: Receipt[], params: GetReceiptsParams): Receipt[] {
  return receipts.filter((x) => {
    const customerIds = params.customerId?.split(',').map(Number);
    const creditControllerIds = params.creditControllerId?.split(',');
    const allocationStatus = params.metricAllocationStatus ?? params.allocationStatus;
    const allocationStatuses = allocationStatus?.split(',');
    const currencies = params.currency?.split(',');
    const receiptDate = x.receiptDate.toISOString().slice(0, 10);
    const matchesSearch =
      !params.search ||
      x.receiptNumber.toLowerCase().includes(params.search.toLowerCase()) ||
      x.customerName.toLowerCase().includes(params.search.toLowerCase()) ||
      x.customerAccountNumber.toLowerCase().includes(params.search.toLowerCase());
    const matchesCustomer = !customerIds || customerIds.includes(x.customerId);
    const matchesCreditController =
      !creditControllerIds ||
      (x.creditControllerId !== undefined && creditControllerIds.includes(x.creditControllerId));
    const matchesStatus =
      !allocationStatuses || allocationStatus === 'all' || allocationStatuses.includes(x.allocationStatus);
    const matchesCurrency = !currencies || params.currency === 'all' || currencies.includes(x.currency);
    const matchesDateFrom = !params.dateFrom || receiptDate >= params.dateFrom;
    const matchesDateTo = !params.dateTo || receiptDate <= params.dateTo;
    // Amount ranges match the values shown in the grid (transaction currency), as the
    // live query does; the not-zero check uses the base amount, which is what decides
    // whether anything is still unapplied.
    const matchesAmountMin = params.amountMin === undefined || x.receivedAmount >= params.amountMin;
    const matchesAmountMax = params.amountMax === undefined || x.receivedAmount <= params.amountMax;
    const matchesOutstandingNotZero = !params.outstandingNotZero || (x.homeUnappliedAmount ?? x.unappliedAmount) !== 0;
    const matchesOutstandingMin = params.outstandingMin === undefined || x.unappliedAmount >= params.outstandingMin;
    const matchesOutstandingMax = params.outstandingMax === undefined || x.unappliedAmount <= params.outstandingMax;
    const matchesPromise = params.hasPromise === undefined || x.hasPromise === params.hasPromise;
    const matchesExternalDispute = params.onDispute === undefined || x.onDispute === params.onDispute;
    const matchesDispute = params.hasDispute === undefined || x.hasDispute === params.hasDispute;

    return (
      matchesSearch &&
      matchesCustomer &&
      matchesCreditController &&
      matchesStatus &&
      matchesCurrency &&
      matchesDateFrom &&
      matchesDateTo &&
      matchesAmountMin &&
      matchesAmountMax &&
      matchesOutstandingNotZero &&
      matchesOutstandingMin &&
      matchesOutstandingMax &&
      matchesPromise &&
      matchesExternalDispute &&
      matchesDispute
    );
  });
}

// --- Result types ---

export interface ReceiptsListResult {
  receipts: Receipt[];
  pagination: PaginationResult;
}

// --- Hooks ---

function useReceiptCountGraphQL(params?: GetReceiptsParams) {
  const apiConfig = useApiConfig();
  const filter = buildGraphQLFilterVariable(params ?? {});
  const queryKey = buildReceiptFilterQueryKey(params);

  return useModeAwareQuery<number, Error>(
    ['receipts-graphql-count', queryKey],
    async ({ signal }) => {
      const variables: Record<string, unknown> = {};
      if (filter) {
        variables.filter = filter;
      }

      const response = await graphqlQuery<ReceiptCountGraphQLResponse>(
        apiConfig,
        RECEIPT_COUNT_QUERY,
        variables,
        signal,
      );

      return response.receipts.receiptCount;
    },
    0,
    {
      staleTime: 30_000,
      refetchOnMount: 'always',
    },
  );
}

export function useReceiptsListGraphQL(params?: GetReceiptsParams, homeCurrency?: string) {
  const apiConfig = useApiConfig();
  const allMockReceipts = getMockReceiptsFromStore();
  const queryParams: GetReceiptsParams = { page: 1, pageSize: 20, sortBy: 'receiptDate', sortOrder: 'desc', ...params };
  const mockPage = queryParams.page ?? 1;
  const mockPageSize = queryParams.pageSize ?? 20;
  const filteredMock = filterMockReceipts(allMockReceipts, queryParams);
  const mockPaged = filteredMock.slice((mockPage - 1) * mockPageSize, mockPage * mockPageSize);
  const mockPagination = computeMockPagination(filteredMock.length, mockPage, mockPageSize);
  const mockResult: ReceiptsListResult = { receipts: mockPaged, pagination: mockPagination };

  return useModeAwareQuery<ReceiptsListResult, Error>(
    ['receipts-graphql-list', queryParams],
    async ({ signal }) => {
      const page = queryParams.page ?? 1;
      const pageSize = queryParams.pageSize ?? 20;

      const variables: Record<string, unknown> = {
        first: pageSize,
        sort: buildGraphQLSortVariable(queryParams.sortBy, queryParams.sortOrder),
        filter: buildGraphQLFilterVariable(queryParams),
      };

      if (page > 1) {
        variables.after = pageToCursor(page);
      }

      const response = await graphqlQuery<ReceiptListGraphQLResponse>(apiConfig, RECEIPT_LIST_QUERY, variables, signal);

      const connection = response.receipts.receiptList;
      const receipts = connection.edges.map((x) => mapGraphQLNodeToReceipt(x.node, homeCurrency));

      const pagination: PaginationResult = {
        page,
        pageSize,
        totalItems: receipts.length,
        totalPages: 1,
        hasNextPage: connection.pageInfo.hasNextPage,
        hasPreviousPage: connection.pageInfo.hasPreviousPage,
      };

      return { receipts, pagination };
    },
    mockResult,
    {
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );
}

export function calculateMockReceiptTotals(filteredMock: Receipt[]): ReceiptTotalsData {
  const unallocated = filteredMock.filter((x) => x.allocationStatus === 'unallocated');
  const fullyAllocated = filteredMock.filter((x) => x.allocationStatus === 'fully_allocated');
  const partiallyAllocated = filteredMock.filter((x) => x.allocationStatus === 'partially_allocated');
  return {
    totalCount: filteredMock.length,
    totalAmount: filteredMock.reduce((sum, x) => sum + x.receivedAmount, 0),
    totalOutstanding: filteredMock.reduce((sum, x) => sum + x.unappliedAmount, 0),
    unallocatedCount: unallocated.length,
    unallocatedAmount: unallocated.reduce((sum, x) => sum + x.receivedAmount, 0),
    fullyAllocatedCount: fullyAllocated.length,
    fullyAllocatedAmount: fullyAllocated.reduce((sum, x) => sum + x.receivedAmount, 0),
    partiallyAllocatedCount: partiallyAllocated.length,
    partiallyAllocatedAmount: partiallyAllocated.reduce((sum, x) => sum + x.receivedAmount, 0),
    partiallyAllocatedOutstandingAmount: partiallyAllocated.reduce(
      (sum, x) => sum + (x.homeUnappliedAmount ?? x.unappliedAmount),
      0,
    ),
    totalCurrencyAmount: filteredMock.reduce((sum, x) => sum + x.receivedAmount, 0),
    unallocatedCurrencyAmount: unallocated.reduce((sum, x) => sum + x.receivedAmount, 0),
    fullyAllocatedCurrencyAmount: fullyAllocated.reduce((sum, x) => sum + x.receivedAmount, 0),
    partiallyAllocatedCurrencyAmount: partiallyAllocated.reduce((sum, x) => sum + x.receivedAmount, 0),
    partiallyAllocatedOutstandingCurrencyAmount: partiallyAllocated.reduce((sum, x) => sum + x.unappliedAmount, 0),
  };
}

export function useReceiptTotalsGraphQL(params?: GetReceiptsParams) {
  const apiConfig = useApiConfig();
  const filter = buildGraphQLFilterVariable(params ?? {});
  const queryKey = buildReceiptFilterQueryKey(params);

  const allMockReceipts = getMockReceiptsFromStore();
  const filteredMock = filterMockReceipts(allMockReceipts, params ?? {});

  const mockTotals: ReceiptTotalsData = calculateMockReceiptTotals(filteredMock);

  return useModeAwareQuery<ReceiptTotalsData, Error>(
    ['receipts-graphql-totals', queryKey],
    async ({ signal }) => {
      const variables: Record<string, unknown> = {};
      if (filter) {
        variables.filter = filter;
      }

      const response = await graphqlQuery<ReceiptTotalsGraphQLResponse>(
        apiConfig,
        RECEIPT_TOTALS_QUERY,
        variables,
        signal,
      );

      return response.receipts.receiptTotals;
    },
    mockTotals,
    {
      staleTime: 30_000,
      refetchOnMount: 'always',
    },
  );
}

// --- Single receipt by ID hook ---

const RECEIPT_BY_ID_QUERY = `
  query ReceiptById($first: Int, $filter: ReceiptListFilterInput) {
    receipts {
      receiptList(first: $first, filter: $filter) {
        edges {
          node {
            ${RECEIPT_NODE_FIELDS}
          }
        }
      }
    }
  }
`;

export function useReceiptByIdGraphQL(receiptId: number | undefined, homeCurrency?: string) {
  const apiConfig = useApiConfig();
  const allMockReceipts = getMockReceiptsFromStore();
  const mockReceipt = allMockReceipts.find((r) => r.id === receiptId) ?? null;

  return useModeAwareQuery<Receipt | null, Error>(
    ['receipt-graphql-by-id', receiptId],
    async ({ signal }) => {
      if (!receiptId) return null;

      const variables = {
        first: 1,
        filter: { transactionId: { eq: receiptId } },
      };

      const response = await graphqlQuery<ReceiptListGraphQLResponse>(
        apiConfig,
        RECEIPT_BY_ID_QUERY,
        variables,
        signal,
      );

      const edges = response.receipts.receiptList.edges;
      if (edges.length === 0) return null;
      return mapGraphQLNodeToReceipt(edges[0].node, homeCurrency);
    },
    mockReceipt,
    {
      enabled: !!receiptId,
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );
}

// --- Composite page data hook ---

const defaultPagination = createDefaultPagination(25);

const defaultTotals: ReceiptTotalsData = {
  totalCount: 0,
  totalAmount: 0,
  totalOutstanding: 0,
  unallocatedCount: 0,
  unallocatedAmount: 0,
  fullyAllocatedCount: 0,
  fullyAllocatedAmount: 0,
  partiallyAllocatedCount: 0,
  partiallyAllocatedAmount: 0,
  partiallyAllocatedOutstandingAmount: 0,
  totalCurrencyAmount: 0,
  unallocatedCurrencyAmount: 0,
  fullyAllocatedCurrencyAmount: 0,
  partiallyAllocatedCurrencyAmount: 0,
  partiallyAllocatedOutstandingCurrencyAmount: 0,
};

export function useReceiptsPageDataGraphQL(params?: GetReceiptsParams, homeCurrency?: string) {
  const listQuery = useReceiptsListGraphQL(params, homeCurrency);
  const countQuery = useReceiptCountGraphQL(params);
  const totalsQuery = useReceiptTotalsGraphQL(buildReceiptTotalsParams(params));

  const basePagination = listQuery.data?.pagination ?? defaultPagination;
  const totalCount = countQuery.data ?? 0;
  const pagination = computePaginationWithCount(basePagination, totalCount, params?.pageSize);

  return {
    receipts: listQuery.data?.receipts ?? [],
    pagination,
    totals: totalsQuery.data ?? defaultTotals,
    isLoading: listQuery.isLoading,
    error: listQuery.error,
    refetch: () => {
      listQuery.refetch();
      countQuery.refetch();
      totalsQuery.refetch();
    },
  };
}
